import { SiteRating, isDefined, isUndefined, schAloftArea } from '@aloft/toolkit-shared';
import { ProductColor, colors } from '@aloft/toolkit-sui';

import {
  Bar,
  CartesianGrid,
  ComposedChart,
  DefaultLegendContent,
  DefaultLegendContentProps,
  LabelList,
  LabelProps,
  Legend,
  Line,
} from 'recharts';

import { selectCompSequence } from '../../../redux/features/selectors/orderSelectors.ts';
import { selectSiteEntities } from '../../../redux/features/selectors/propertyDataSelectors/siteSelectors.ts';
import { selectSiteValues } from '../../../redux/features/selectors/selectSiteValue.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { GraphTitle } from '../components/GraphTitle.tsx';
import { GraphXAxis } from '../components/GraphXAxis.tsx';
import { GraphYAxis } from '../components/GraphYAxis.tsx';
import { ResponsiveGraphWrapper } from '../components/ResponsiveGraphWrapper.tsx';
import { SimpleGraphTooltip } from '../components/SimpleGraphTooltip.tsx';
import { GraphProps } from '../types.ts';

type ChartData = {
  propertyName: string;
  label: string;
  influenceRating?: SiteRating;
  viewRating?: SiteRating;
  siteSize?: number;
  siteValue?: number;
  compNumber?: number;
};

const getRatingColor = (rating?: SiteRating): ProductColor => {
  switch (rating) {
    case 'Adverse':
      return 'graphAdverse';
    case 'Neutral':
      return 'graphNeutral';
    case 'Beneficial':
    default:
      return 'graphBeneficial';
  }
};

const CustomLegend = ({ payload, ref, ...rest }: DefaultLegendContentProps) => (
  <DefaultLegendContent
    payload={[
      { value: 'Site Size', type: 'line', color: colors.graphChart3 },
      { value: 'Site Value', type: 'square', color: colors.graphChart },
      { value: 'A - Adverse', type: 'square', color: colors.graphAdverse },
      { value: 'N - Neutral', type: 'square', color: colors.graphNeutral },
      { value: 'B - Beneficial', type: 'square', color: colors.graphBeneficial },
    ]}
    {...rest}
  />
);

const CustomLabelList = ({ x, y, width, value }: LabelProps) => {
  if (isUndefined(x) || isUndefined(y) || isUndefined(width) || !value) return null;

  const boxWidth = 28;
  const boxHeight = 18;
  const cx = Number(x) + Number(width) / 2 - boxWidth / 2;
  const cy = Number(y) - boxHeight / 2;

  let color = colors.graphBeneficial;
  if (String(value).includes('A')) color = colors.graphAdverse;
  else if (String(value).includes('N')) color = colors.graphNeutral;

  return (
    <g>
      <rect
        x={cx}
        y={cy}
        rx={3}
        ry={3}
        width={boxWidth}
        height={boxHeight}
        fill={colors.graphBackground}
        stroke={color}
      />
      <text
        x={cx + boxWidth / 2}
        y={cy + boxHeight / 2 + 4}
        textAnchor="middle"
        fontSize="12"
        fill={color}
      >
        {value}
      </text>
    </g>
  );
};

export const SiteSizeVsSiteValueGraph = ({ disableAnimations, width, height }: GraphProps) => {
  const compSequence = useAppSelector(selectCompSequence);
  const siteEntities = useAppSelector(selectSiteEntities);
  const siteValues = useAppSelector(selectSiteValues);

  const data: ChartData[] = ['subject', ...compSequence]
    .map((id, index) => {
      const isSubject = id === 'subject';
      const compNumber = isSubject ? undefined : index;

      const { area, influenceRating, viewRating } = siteEntities[id] ?? {};
      const siteValue = siteValues[id];

      if (!area || !siteValue) return undefined;

      return {
        propertyName: isSubject ? 'Subject' : `Comp ${compNumber}`,
        influenceRating,
        viewRating,
        label: `${influenceRating?.charAt(0).toLocaleUpperCase() ?? '-'}${viewRating?.charAt(0).toLocaleUpperCase() ?? '-'}`,
        siteSize: schAloftArea.parse(area).getUnitAmount('sqft'),
        siteValue: siteValue.toDollarLike(),
        compNumber,
      };
    })
    .filter(isDefined);

  return (
    <ResponsiveGraphWrapper disableAnimations={disableAnimations} width={width} height={height}>
      <ComposedChart
        data={data}
        width={width}
        height={height}
        margin={{ top: 48, left: 8, bottom: 8, right: 8 }}
        style={{ background: colors.graphBackground, borderRadius: 8 }}
      >
        <GraphTitle>Site Size vs Site Value</GraphTitle>
        <CartesianGrid vertical={false} yAxisId="siteSize" />
        <Legend content={CustomLegend} />

        <SimpleGraphTooltip<ChartData>
          disableAnimations={disableAnimations}
          titleKey="propertyName"
          rows={[
            {
              label: 'Site Size',
              key: 'siteSize',
              color: 'graphChart3',
              valueFormatter: (v) => (v ? `${v.toLocaleString()} SF` : '-'),
            },
            {
              label: 'Site Value',
              key: 'siteValue',
              color: 'graphChart',
              valueFormatter: (v) => (v ? `$${v.toLocaleString()}` : '-'),
            },
            {
              label: 'Influence Rating',
              key: 'influenceRating',
              color: ({ influenceRating }) => getRatingColor(influenceRating),
            },
            {
              label: 'View Rating',
              key: 'viewRating',
              color: ({ viewRating }) => getRatingColor(viewRating),
            },
          ]}
        />

        <GraphXAxis dataKey="propertyName" />
        <GraphYAxis
          yAxisId="siteSize"
          label="Site Size (SF)"
          tickFormatter={(val: number) => val.toLocaleString()}
        />
        <GraphYAxis
          yAxisId="siteValue"
          position="right"
          label="Site Value"
          tickFormatter={(val: number) => `$${val.toLocaleString()}`}
        />

        <Bar
          name="Site Value"
          dataKey="siteValue"
          yAxisId="siteValue"
          fill={colors.graphChart}
          isAnimationActive={!disableAnimations}
        >
          <LabelList dataKey="label" content={CustomLabelList} />
        </Bar>
        <Line
          name="Site Size"
          dataKey="siteSize"
          yAxisId="siteSize"
          stroke={colors.graphChart3}
          isAnimationActive={!disableAnimations}
        />
      </ComposedChart>
    </ResponsiveGraphWrapper>
  );
};
